import type { ReactElement, ReactNode } from "react";

import { ChangeCell, SparklineCell, TextCell } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";

import { NO_DATA_SIGN, NOT_AVAILABLE } from "app/filters/const";
import { dynamicFilterFilter } from "app/filters/dynamicFilter";
import {
    abbrNumberWithThresholdFilter,
    changeFilter,
    percentageSignFilter,
    prefixNumberFilter,
    swNumberFilter,
    type ThresholdOptions,
    timeFilter,
} from "app/filters/ngFilters";
import { NO_DATA, NO_DATA_HYPHEN } from "app/pages/sales-intelligence/constants/common";
import type { TaxonomyPathDisplayNames } from "app/pages/sales-intelligence/types/taxonomy";
import { formatTaxonomyPathDisplayNames } from "app/pages/sales-intelligence/utils/taxonomy-tree/path";
import dateTimeService from "app/services/date-time/dateTimeService";
import { WHITESPACE_MMMM_YYYY } from "app/services/date-time/formats";
import type { RawDate } from "app/UtilitiesAndConstants/types/dates";

import { StyledTruncatedTextCell } from "../components/atoms/StyledTruncatedTextCell";
import ProgressBarCell from "../components/ProgressBarCell/ProgressBarCell";
import RatingCell from "../components/RatingCell/RatingCell";
import type { PrimitiveListCellValue, TrendItem } from "../types";
import {
    getChangeFormattedValue,
    getListFormatValue,
    getPercentageRoundedValue,
    trendItemsToSparklineData,
    truncateToDecimalPlaces,
} from "../utils/cellValue";

const FORMAT_DECIMAL_PLACE = 2;

const abbrNumberWithThreshold = abbrNumberWithThresholdFilter();
const timeFormatterFilter = timeFilter();
const percentageFormatterFilter = percentageSignFilter();
const prefixNumberFormatterFilter = prefixNumberFilter();
const dynamicFormatterFilter = dynamicFilterFilter();
const swNumberFormatterFilter = swNumberFilter();
const changeFormatterFilter = changeFilter();

export const renderRatingCellFactory = (fallback: string | number = NO_DATA) =>
    function (value: number | null): ReactElement {
        if (value === null) {
            return <TextCell>{fallback}</TextCell>;
        }

        return <RatingCell>{truncateToDecimalPlaces(value, FORMAT_DECIMAL_PLACE)}</RatingCell>;
    };

export const renderDefaultTextCell = <V extends ReactNode>(value: V) => <TextCell>{value}</TextCell>;

export const renderAbbrNumberWithThresholdCellFactory = (threshold: ThresholdOptions) =>
    function (value: unknown) {
        return <TextCell>{abbrNumberWithThreshold(threshold, value as string)}</TextCell>;
    };

export const renderDateCellFactory = (format: string = WHITESPACE_MMMM_YYYY) =>
    function <V extends RawDate>(value: V) {
        return <TextCell dataAutomation="date-cell">{dateTimeService.formatWithMoment(value, format)}</TextCell>;
    };

export const renderIndustryCellFactory = <V extends string[]>(value: V) => {
    const { formattedItems } = getListFormatValue(value ?? []);

    return <TextCell dataAutomation="industry-cell">{formattedItems}</TextCell>;
};

export const renderTaxonomyPathDisplayNamesCellFactory = <V extends TaxonomyPathDisplayNames>(value?: V) => (
    <StyledTruncatedTextCell dataAutomation="taxonomy-path-cell">
        {formatTaxonomyPathDisplayNames(value)}
    </StyledTruncatedTextCell>
);

export const renderSiteTypeCellFactory = <V extends string[]>(value: V) => {
    const { formattedItems } = getListFormatValue(value);

    return <TextCell dataAutomation="site-type-cell">{formattedItems}</TextCell>;
};

export const renderTimeCellFactory = <V extends number>(value: V) => (
    <TextCell dataAutomation="time-cell">{timeFormatterFilter(value, null)}</TextCell>
);

export const renderPercentageBarCellFactory = <V extends readonly [number, number]>(value?: V) => {
    const { rightValue, leftValue, hasValues } = getPercentageRoundedValue(value);

    return (
        <ProgressBarCell
            value={hasValues ? value?.[0] || 0 : leftValue}
            ChangeValueProps={{ unsigned: true, value: rightValue }}
            primaryColor={colorsPalettes.blue[400]}
            secondaryColor={colorsPalettes.sky[300]}
        />
    );
};

export const renderProgressBarCellFactory = <V extends number>(value: V) => (
    <ProgressBarCell
        value={value}
        hideChangeValue
        barWidth={50}
        primaryColor={colorsPalettes.blue[400]}
        secondaryColor={colorsPalettes.sky[300]}
    />
);

export const renderPercentageStaticCellFactory = (value: number | string | null) => {
    const displayedValue: string = percentageFormatterFilter(value, FORMAT_DECIMAL_PLACE, NO_DATA_HYPHEN);
    const isNoData = displayedValue === NO_DATA_HYPHEN;

    return <TextCell color={isNoData ? colorsPalettes.carbon[250] : undefined}>{displayedValue}</TextCell>;
};

export const renderRankCellFactory = <V extends PrimitiveListCellValue>(
    value: V,
    format: string = "number",
    undefinedSign: string = NO_DATA_SIGN,
) => {
    return (
        <TextCell dataAutomation="rank-cell">
            {prefixNumberFormatterFilter(dynamicFormatterFilter(value, format, undefinedSign), "#")}
        </TextCell>
    );
};

export const renderRoundedNumberCellFactory = <V extends PrimitiveListCellValue>(value: V) => {
    return <TextCell>{swNumberFormatterFilter(value || 0, 2)}</TextCell>;
};

type AbsValueFormatter = (absValue: number) => string;

const renderChangeCell = (value: number | null, formatAbs: AbsValueFormatter) => {
    const formattedValue = getChangeFormattedValue(value, () => formatAbs(Math.abs(value as number)));
    return (
        <ChangeCell
            value={value ?? undefined}
            formattedValue={formattedValue}
        />
    );
};

export const renderPercentageCellFactory = (value: number | null) => renderChangeCell(value, changeFormatterFilter);

export const renderPercentageCellWithThresholdFactory =
    (minThreshold?: number) =>
    <V extends number>(value: V) =>
        renderChangeCell(value, (abs) =>
            minThreshold && abs < minThreshold ? `<${Math.round(minThreshold * 100)}%` : changeFormatterFilter(abs, 0),
        );

export const renderPercentageCellWithMaxCapFactory =
    (maxCapPercent: number) =>
    <V extends number>(value: V) =>
        renderChangeCell(value, (abs) =>
            abs > maxCapPercent / 100 ? ` > ${maxCapPercent.toLocaleString("en-US")}%` : changeFormatterFilter(abs),
        );

export const renderTrafficChangeFactory = (value: number) =>
    renderChangeCell(value, (abs) => changeFormatterFilter(abs));

export const renderSparklineCellFactory = (value: readonly TrendItem[]) => {
    if (!value || value.length === 0) {
        return <TextCell>{NOT_AVAILABLE}</TextCell>;
    }

    const data = trendItemsToSparklineData(value);

    if (data.length === 0) {
        return <TextCell>{NOT_AVAILABLE}</TextCell>;
    }

    return (
        <SparklineCell
            type="area"
            data={data as number[]}
            height={30}
            min={0}
        />
    );
};
